import { useMemo, useState } from 'react';
import { Ticket, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePortalPunchCards, type PortalPunchCardItem, type PunchCardStatus } from '@/hooks/usePortalPunchCards';

export function PortalPunchCards() {
  const { t } = useTranslation();
  const { data: punchCards = [], isLoading, error } = usePortalPunchCards();
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'used'>('all');

  const filteredCards = useMemo(() => {
    if (activeTab === 'active') {
      return punchCards.filter((card) => card.status === 'active');
    }
    if (activeTab === 'used') {
      return punchCards.filter((card) => card.status === 'used' || card.status === 'expired');
    }
    return punchCards;
  }, [punchCards, activeTab]);

  const counts = useMemo(() => {
    const active = punchCards.filter((card) => card.status === 'active').length;
    const used = punchCards.filter((card) => card.status === 'used' || card.status === 'expired').length;
    return { all: punchCards.length, active, used };
  }, [punchCards]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <div className="flex items-center text-red-800 dark:text-red-200">
          <AlertCircle className="mr-2 h-5 w-5" />
          <span>{t('portal.punch_cards.error')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('portal.punch_cards.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('portal.punch_cards.subtitle')}
        </p>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {t('portal.punch_cards.tab_all')} ({counts.all})
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {t('portal.punch_cards.tab_active')} ({counts.active})
          </button>
          <button
            onClick={() => setActiveTab('used')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'used'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {t('portal.punch_cards.tab_used')} ({counts.used})
          </button>
        </nav>
      </div>

      {filteredCards.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <Ticket className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            {activeTab === 'all'
              ? t('portal.punch_cards.empty_title')
              : activeTab === 'active'
              ? t('portal.punch_cards.empty_active')
              : t('portal.punch_cards.empty_used')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'all'
              ? t('portal.punch_cards.empty_subtitle')
              : activeTab === 'active'
              ? t('portal.punch_cards.empty_active_subtitle')
              : t('portal.punch_cards.empty_used_subtitle')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCards.map((card) => (
            <PunchCardCard key={card.id} card={card} />
          ))}
        </div>
      )}
    </div>
  );
}

interface PunchCardCardProps {
  card: PortalPunchCardItem;
}

function PunchCardCard({ card }: PunchCardCardProps) {
  const { t } = useTranslation();
  const remainingClips = card.total_clips - card.used_clips;
  const progressPercent = card.total_clips > 0 ? (remainingClips / card.total_clips) * 100 : 0;

  const statusColors: Record<PunchCardStatus, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    used: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    expired: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };

  const statusLabels: Record<PunchCardStatus, string> = {
    active: t('portal.punch_cards.status_active'),
    used: t('portal.punch_cards.status_used'),
    expired: t('portal.punch_cards.status_expired'),
    cancelled: t('portal.punch_cards.status_cancelled'),
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                <Ticket className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {card.name}
                </h3>
                {card.punch_card_number && (
                  <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                    #{card.punch_card_number}
                  </p>
                )}
              </div>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusColors[card.status]}`}
            >
              {statusLabels[card.status]}
            </span>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                {t('portal.punch_cards.remaining')}
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {remainingClips} / {card.total_clips} {t('portal.punch_cards.clips')}
              </span>
            </div>
            <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all ${
                  card.status === 'active'
                    ? 'bg-indigo-600 dark:bg-indigo-500'
                    : 'bg-gray-400 dark:bg-gray-500'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                {t('portal.punch_cards.purchased')}:
              </span>{' '}
              <span className="text-gray-900 dark:text-white">
                {formatDate(card.purchased_at)}
              </span>
            </div>
            {card.expires_at && (
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  {t('portal.punch_cards.expires')}:
                </span>{' '}
                <span
                  className={`${
                    card.status === 'expired'
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {formatDate(card.expires_at)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('da-DK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
